/**
 * Frame draw lists (W1-05b engine-graph; rendering.md R§3, R§8, R§10, ARCHITECTURE.md §7.3).
 *
 * The engine owns FrameDrawList pooling: `createFrameList()` makes a list whose arrays, note batch and line-draw
 * objects are reused by every later `Engine.frame(t, opts, list)` (consumers never retain anything from a list).
 * The seams fill their parts (W1-06 notes, hit effects, HUD, ghosts; W1-08 effects and videos); this module fills
 * the line bodies, in Phira's (zOrder, index) order with attachUI lines excluded (R§3.10, R§8 step 4):
 *
 *  - normal (`line.png`): colour = colour events (opaque) or `res.judge_line_color` with `a × res.alpha` (R§3.4,
 *    the AP/FC indicator: the respack Perfect colour in autoplay and the editor; in judged play Perfect while no
 *    Good/Bad/Miss is committed, Good with Goods only, otherwise white), then `a × max(alpha · res.alpha, 0)`;
 *    a quad from -lineLength to lineLength, thickness 0.01, or 0.0076 when |scaleY - 1| > 1e-4 in f32 (the raw
 *    scaleY), under `line matrix · Scale(sx, sy)`;
 *  - texture / gif: colour RGB from colour events (default white), `a = max(alpha · res.alpha, 0)` (replaces); the
 *    unit quad under `line matrix · Scale(sx · w, sy · h)` (w, h = texture pixels; sx, sy already carry the
 *    2/1350 factor); GIF lines carry Phira's frame position `gifMs`; textures without known pixel size are not
 *    drawn (the asset is missing or not decoded);
 *  - text: colour events or white, `a = max(alpha · res.alpha, 0)`; `m = line matrix · Scale(sx, -sy)`: the
 *    renderer lays the text block out in world units with y pointing down (upright text), em = 0.08 world units,
 *    anchored at its centre (R§3.6);
 *  - paint: a marker telling the renderer to composite the line's paint buffer here (fed by `paintStamps`).
 * Lines Phira draws nothing for (`drawsNothing`: opacity <= 0, zero scale, empty text) and fully transparent
 * bodies are left out; the result on screen is identical and the renderer saves the draw calls. Paint lines are
 * always listed (their persistent buffer stays visible).
 *
 * Preview filters (never exported): hidden lines and, while a solo set exists, lines outside it are not drawn;
 * `dim` multiplies the alpha of every line but the active one by (1 - factor), clamped to 0..1 like the notes
 * (W1-06), so lines and notes of a dimmed line always fade together.
 */

import { gifMsFromPhase, type LineStateBuf } from './lineState';
import { createNoteQuadBatch } from './notes/index';
import { judgedCounts } from './notes/judgedHud';
import type { LineDrawInfo } from './graph';
import type {
  EngineCompileContext,
  EngineEffectsModule,
  EngineNotesModule,
  FrameDrawList,
  FrameDrawListBuilder,
  FrameOptions,
  LineDraw,
  PreviewFilters,
  SkinMetrics,
  Viewport,
} from './types';
import type { AssetId, LineId, PackageSettings } from '../model/types';
import type { MutMat2D, RGBAf } from '../util/types';

const fr = Math.fround;
const F32_1E4 = fr(1e-4);

/** Line thickness in world units (R§3.4). */
export const LINE_THICKNESS = 0.01;
/** Thickness once scaleY differs from 1 (the Phira quirk). */
export const LINE_THICKNESS_SCALED = 0.0076;

type Rgba = [number, number, number, number];

interface NormalDraw {
  kind: 'normal';
  line: LineId;
  m: MutMat2D;
  halfLen: number;
  thickness: number;
  color: Rgba;
}
interface TextureDraw {
  kind: 'texture';
  line: LineId;
  m: MutMat2D;
  asset: AssetId;
  color: Rgba;
}
interface GifDraw {
  kind: 'gif';
  line: LineId;
  m: MutMat2D;
  asset: AssetId;
  gifMs: number;
  color: Rgba;
}
interface TextDraw {
  kind: 'text';
  line: LineId;
  m: MutMat2D;
  text: string;
  color: Rgba;
}
interface PaintDraw {
  kind: 'paint';
  line: LineId;
}

/** Reusable line-draw objects of one frame list (one free list per kind). */
class LineDrawPool {
  private readonly normal: NormalDraw[] = [];
  private readonly texture: TextureDraw[] = [];
  private readonly gif: GifDraw[] = [];
  private readonly text: TextDraw[] = [];
  private readonly paint: PaintDraw[] = [];
  private nNormal = 0;
  private nTexture = 0;
  private nGif = 0;
  private nText = 0;
  private nPaint = 0;

  reset(): void {
    this.nNormal = 0;
    this.nTexture = 0;
    this.nGif = 0;
    this.nText = 0;
    this.nPaint = 0;
  }

  takeNormal(line: LineId): NormalDraw {
    let d = this.normal[this.nNormal];
    if (d === undefined) {
      d = { kind: 'normal', line, m: [1, 0, 0, 1, 0, 0], halfLen: 0, thickness: 0, color: [0, 0, 0, 0] };
      this.normal.push(d);
    }
    this.nNormal++;
    d.line = line;
    return d;
  }

  takeTexture(line: LineId, asset: AssetId): TextureDraw {
    let d = this.texture[this.nTexture];
    if (d === undefined) {
      d = { kind: 'texture', line, m: [1, 0, 0, 1, 0, 0], asset, color: [0, 0, 0, 0] };
      this.texture.push(d);
    }
    this.nTexture++;
    d.line = line;
    d.asset = asset;
    return d;
  }

  takeGif(line: LineId, asset: AssetId): GifDraw {
    let d = this.gif[this.nGif];
    if (d === undefined) {
      d = { kind: 'gif', line, m: [1, 0, 0, 1, 0, 0], asset, gifMs: 0, color: [0, 0, 0, 0] };
      this.gif.push(d);
    }
    this.nGif++;
    d.line = line;
    d.asset = asset;
    return d;
  }

  takeText(line: LineId): TextDraw {
    let d = this.text[this.nText];
    if (d === undefined) {
      d = { kind: 'text', line, m: [1, 0, 0, 1, 0, 0], text: '', color: [0, 0, 0, 0] };
      this.text.push(d);
    }
    this.nText++;
    d.line = line;
    return d;
  }

  takePaint(line: LineId): PaintDraw {
    let d = this.paint[this.nPaint];
    if (d === undefined) {
      d = { kind: 'paint', line };
      this.paint.push(d);
    }
    this.nPaint++;
    d.line = line;
    return d;
  }
}

const pools = new WeakMap<object, LineDrawPool>();

function poolOf(list: object): LineDrawPool {
  let p = pools.get(list);
  if (p === undefined) {
    p = new LineDrawPool();
    pools.set(list, p);
  }
  return p;
}

const EMPTY_VIEWPORT: Viewport = { winW: 0, winH: 0, vx: 0, vy: 0, W: 0, H: 0, ar: 16 / 9 };

/** A fresh, empty frame draw list whose buffers later frames reuse. */
export function createFrameList(): FrameDrawListBuilder {
  const list: FrameDrawListBuilder = {
    t: 0,
    viewport: EMPTY_VIEWPORT,
    resAlpha: 1,
    background: { illustration: null, dim: 0 },
    videos: [],
    lines: [],
    notes: createNoteQuadBatch(),
    effects: [],
    hitFx: [],
    particles: [],
    hud: null,
    globalEffects: [],
    ghosts: [],
  };
  poolOf(list);
  return list;
}

/**
 * `out` as a writable builder with emptied arrays (a list from `createFrameList` or a previous frame; a list built
 * elsewhere is adopted as long as its arrays are plain arrays).
 */
export function reuseFrameList(out: FrameDrawList): FrameDrawListBuilder {
  const b = out as FrameDrawListBuilder;
  b.videos.length = 0;
  b.lines.length = 0;
  b.effects.length = 0;
  b.hitFx.length = 0;
  b.particles.length = 0;
  b.globalEffects.length = 0;
  b.ghosts.length = 0;
  b.notes.count = 0;
  b.notes.buckets.length = 0;
  b.hud = null;
  return b;
}

/** Rust `f32::max(v, 0)`: NaN gives 0. */
function nonNeg(v: number): number {
  return v > 0 ? v : 0;
}

/**
 * Preview dim multiplier of a line body: 1 for the active line (or without dim), else `1 - factor` clamped to
 * 0..1 (W1-06 clamps the notes the same way); a NaN factor gives 0 (the body is not drawn).
 */
export function lineDimMultiplier(dim: PreviewFilters['dim'], line: LineId): number {
  if (dim === null || dim.activeLine === line) return 1;
  const m = 1 - dim.factor;
  return m >= 1 ? 1 : m > 0 ? m : 0;
}

/** `m · Scale(sx, sy)` into `out`. */
function scaled(m: readonly number[], sx: number, sy: number, out: MutMat2D): MutMat2D {
  out[0] = (m[0] ?? 1) * sx;
  out[1] = (m[1] ?? 0) * sx;
  out[2] = (m[2] ?? 0) * sy;
  out[3] = (m[3] ?? 1) * sy;
  out[4] = m[4] ?? 0;
  out[5] = m[5] ?? 0;
  return out;
}

function setColor(out: Rgba, r: number, g: number, b: number, a: number): void {
  out[0] = r;
  out[1] = g;
  out[2] = b;
  out[3] = a;
}

/** Thickness of a normal line from its raw scaleY (R§3.4: |scaleY - 1| > 1e-4 in f32). */
export function lineThickness(scaleYRaw: number | null): number {
  if (scaleYRaw === null) return LINE_THICKNESS;
  return Math.abs(fr(fr(scaleYRaw) - 1)) > F32_1E4 ? LINE_THICKNESS_SCALED : LINE_THICKNESS;
}

/** `res.judge_line_color` once Bad or Miss judgements exist (R§3.4). */
export const JUDGE_LINE_WHITE: RGBAf = [1, 1, 1, 1];

/**
 * Counters the AP/FC indicator reads (judged play): Perfect, Good and all committed judgements up to the frame
 * (`committed - perfect - good` = Bad + Miss).
 */
export interface IndicatorCounts {
  readonly perfect: number;
  readonly good: number;
  readonly committed: number;
}

/**
 * `res.judge_line_color` before `a × res.alpha` (R§3.4, Phira's AP/FC indicator, on by default): `null` counts
 * (autoplay, the editor) give the Perfect colour; in judged play Perfect while every committed judgement is a
 * Perfect, Good while Goods exist but no Bad / Miss, otherwise white.
 */
export function judgeLineColor(
  skin: Pick<SkinMetrics, 'colorPerfect' | 'colorGood'>,
  counts: IndicatorCounts | null,
  /** Phira `apFcIndicator` (CR-W3-02a-10, R§3.4): the ladder applies only while it is on (default). */
  apFcIndicator = true,
): RGBAf {
  if (counts === null) return skin.colorPerfect;
  if (!apFcIndicator) return JUDGE_LINE_WHITE;
  if (counts.committed - counts.perfect - counts.good > 0) return JUDGE_LINE_WHITE;
  return counts.good > 0 ? skin.colorGood : skin.colorPerfect;
}

/** Per-line drawing data by export index. */
export type DrawInfoAt = (index: number) => LineDrawInfo;

/**
 * Fills `out.lines` with the line bodies of `states` (export order) in draw order `order` (export indices,
 * (zOrder, index) order, attachUI lines excluded). `judgeColor`: the default colour of normal lines
 * (`judgeLineColor`; the respack Perfect colour when omitted, as in autoplay).
 */
export function fillLineDraws(
  out: FrameDrawListBuilder,
  states: readonly LineStateBuf[],
  order: Int32Array,
  info: DrawInfoAt,
  pkg: Pick<PackageSettings, 'lineLength'>,
  opts: Pick<FrameOptions, 'resAlpha' | 'skin' | 'filters'>,
  judgeColor: RGBAf = opts.skin.colorPerfect,
): void {
  const pool = poolOf(out);
  pool.reset();
  const lines: LineDraw[] = out.lines;
  lines.length = 0;
  const filters = opts.filters;
  const solo = filters.soloLines.size > 0;
  const dim = filters.dim;
  const resAlpha = opts.resAlpha;
  const perfect = judgeColor;
  for (const i of order) {
    const s = states[i];
    if (s === undefined) continue;
    const line = s.line;
    if (filters.hiddenLines.has(line) || (solo && !filters.soloLines.has(line))) continue;
    const dimF = lineDimMultiplier(dim, line);
    if (s.kind === 'paint') {
      if (s.uiMover) continue;
      lines.push(pool.takePaint(line));
      continue;
    }
    if (s.drawsNothing !== null) continue;
    const alpha = nonNeg(fr(s.alpha * resAlpha));
    const c = s.color;
    switch (s.kind) {
      case 'normal': {
        // The default colour is res.judge_line_color (a x res.alpha); then a x max(alpha x res.alpha, 0).
        const a = (c === null ? perfect[3] * resAlpha : 1) * alpha * dimF;
        if (!(a > 0)) continue;
        const d = pool.takeNormal(line);
        if (c === null) setColor(d.color, perfect[0], perfect[1], perfect[2], a);
        else setColor(d.color, c[0], c[1], c[2], a);
        scaled(s.m, s.sx, s.sy, d.m);
        d.halfLen = pkg.lineLength;
        d.thickness = lineThickness(s.scaleYRaw);
        lines.push(d);
        break;
      }
      case 'texture':
      case 'gif': {
        const inf = info(i);
        const asset = inf.asset;
        if (asset === null || inf.texW === null || inf.texH === null) continue;
        const a = alpha * dimF;
        if (!(a > 0)) continue;
        if (s.kind === 'gif') {
          const d = pool.takeGif(line, asset);
          setColor(d.color, c?.[0] ?? 1, c?.[1] ?? 1, c?.[2] ?? 1, a);
          scaled(s.m, s.sx * inf.texW, s.sy * inf.texH, d.m);
          d.gifMs = inf.gifTotalMs === null ? 0 : gifMsFromPhase(s.gifProgress, inf.gifTotalMs);
          lines.push(d);
        } else {
          const d = pool.takeTexture(line, asset);
          setColor(d.color, c?.[0] ?? 1, c?.[1] ?? 1, c?.[2] ?? 1, a);
          scaled(s.m, s.sx * inf.texW, s.sy * inf.texH, d.m);
          lines.push(d);
        }
        break;
      }
      case 'text': {
        const text = s.text ?? '';
        const a = alpha * dimF;
        if (text === '' || !(a > 0)) continue;
        const d = pool.takeText(line);
        setColor(d.color, c?.[0] ?? 1, c?.[1] ?? 1, c?.[2] ?? 1, a);
        scaled(s.m, s.sx, -s.sy, d.m);
        d.text = text;
        lines.push(d);
        break;
      }
    }
  }
}

/** What the content of one frame is composed from: the engine graph's nodes, or a full recompile (verify mode). */
export interface FrameSources {
  readonly notes: EngineNotesModule;
  readonly effects: EngineEffectsModule;
  /** Context whose `lineState` evaluates with the frame's aspect ratio and preview filters. */
  readonly ctx: EngineCompileContext;
  /** States of every line at t (export order), evaluated with the frame's aspect ratio and preview filters. */
  readonly states: readonly LineStateBuf[];
  /** Export indices of the drawn line bodies in (zOrder, index) order, attachUI lines excluded. */
  readonly drawOrder: Int32Array;
  readonly drawInfo: DrawInfoAt;
  readonly pkg: Pick<PackageSettings, 'lineLength'>;
}

/**
 * Fills the drawn content of `out` at t in Phira's composition order (R§8): videos, line bodies, notes, effects
 * (non-global; the renderer draws the global ones last), hit effects and particles, HUD; the editor-only ghost
 * outlines come last. `out` must be empty (`createFrameList` / `reuseFrameList`).
 */
export function fillFrameContent(
  out: FrameDrawListBuilder,
  src: FrameSources,
  t: number,
  opts: FrameOptions,
  vp: Viewport,
): void {
  const { notes, effects, ctx, states } = src;
  effects.videos(ctx, t, opts, out);
  const counts = opts.judge === null ? null : judgedCounts(ctx, t, opts.judge);
  fillLineDraws(
    out,
    states,
    src.drawOrder,
    src.drawInfo,
    src.pkg,
    opts,
    judgeLineColor(opts.skin, counts, opts.player.apFcIndicator ?? true),
  );
  notes.pushNoteQuads(ctx, states, t, opts, out);
  effects.effects(ctx, t, opts, out);
  notes.hitFx(ctx, t, opts, out);
  out.hud = opts.showHud ? notes.hud(ctx, states, t, opts, vp) : null;
  if (opts.ghosts) notes.ghosts(ctx, states, t, opts, out);
  else out.ghosts.length = 0;
}
